import { AUTH } from '@/constants/auth'
import { SETTINGS } from '@/constants/settings'
import { Tokens } from '@/interfaces/jwt'
import { Team } from '@/services/team.service'
import Cookies from 'js-cookie'
import { jwtDecode } from 'jwt-decode'
import { create } from 'zustand'

interface AuthUser {
  sub: string
  name: string
  email: string
  avatar: string
  role: string
  exp: number
}

type AuthState = {
  user: AuthUser | null
  tokens: Tokens | null
  activeTeam: Team | null
}

type AuthActions = {
  setUser: (user: AuthState['user']) => void
  setTokens: (accessToken: AuthState['tokens']) => void
  logout: () => void
  refreshAuth: (tokens: Tokens) => void
  setActiveTeam: (team: AuthState['activeTeam']) => void
  getIsOwner: () => boolean
  getIsManager: () => boolean
  getHasFullTeamAccess: () => boolean
}

export type AuthStore = AuthState & AuthActions

export const useAuthStore = create<AuthStore>((set, get) => {
  const access_token = Cookies.get(AUTH.ACCESS_TOKEN)
  const refresh_token = Cookies.get(AUTH.REFRESH_TOKEN)
  let tokens = null
  let user = null
  if (access_token && refresh_token) {
    tokens = {
      access_token,
      refresh_token,
    }
    user = jwtDecode<AuthUser>(access_token)
  }

  return {
    user,
    tokens,
    activeTeam: null,
    getIsOwner: () => {
      const team = get()?.activeTeam
      if (!team) return false
      if (team.isOwner !== undefined) return team.isOwner
      if (team.myRole !== undefined) return team.myRole === 'OWNER'
      if (team.ownerId !== undefined) return get()?.user?.sub === team.ownerId
      return false
    },
    getIsManager: () => get()?.activeTeam?.myRole === 'MANAGER',
    getHasFullTeamAccess: () => {
      const team = get()?.activeTeam
      if (!team) return false
      if (team.isOwner !== undefined && team.isOwner) return true
      return team.myRole === 'OWNER' || team.myRole === 'MANAGER'
    },

    setUser: (user) => set((state) => ({ ...state, user })),
    setTokens: (tokens) =>
      set((state) => {
        if (tokens) {
          Cookies.set(AUTH.ACCESS_TOKEN, tokens.access_token)
          Cookies.set(AUTH.REFRESH_TOKEN, tokens.refresh_token)
        }
        return { ...state, tokens }
      }),
    logout: () =>
      set((state) => {
        Cookies.remove(AUTH.ACCESS_TOKEN)
        Cookies.remove(AUTH.REFRESH_TOKEN)
        Cookies.remove(SETTINGS.TEAM_ID)
        return {
          ...state,
          user: null,
          tokens: null,
          activeTeam: null,
        }
      }),
    refreshAuth: (tokens: Tokens) => {
      Cookies.set(AUTH.ACCESS_TOKEN, tokens.access_token)
      Cookies.set(AUTH.REFRESH_TOKEN, tokens.refresh_token)
      const user = jwtDecode<AuthUser>(tokens.access_token)
      set((state) => ({ ...state, tokens, user }))
    },
    setActiveTeam: (team) => {
      if (team) {
        Cookies.set(AUTH.TEAM_ID, String(team.id))
      }
      set({ activeTeam: team })
    },
  }
})

export const selectUser = (state: AuthState) => state.user
export const selectTokens = (state: AuthState) => state.tokens
export const selectActiveTeam = (state: AuthState) => state.activeTeam
export const selectGetIsOwner = (state: AuthActions) => state.getIsOwner
export const selectGetIsManager = (state: AuthActions) => state.getIsManager
export const selectGetHasFullTeamAccess = (state: AuthActions) =>
  state.getHasFullTeamAccess
export const selectSetActiveTeam = (state: AuthActions) => state.setActiveTeam
export const selectIsAuthenticated = (state: AuthState) => !!state.user
export const selectIsAdmin = (state: AuthState) => state.user?.role === 'admin'
export const selectRefreshAuth = (state: AuthActions) => state.refreshAuth
export const selectLogout = (state: AuthActions) => state.logout
export const selectSetUser = (state: AuthActions) => state.setUser
export const selectSetTokens = (state: AuthActions) => state.setTokens
